import React, { useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import logo from '../../assets/logo.png';
import { LogOut } from 'lucide-react';
import Navigation, { type NavigationRef } from './Navigation';
import AppName from '../ui/AppName';
import { trackUpgradeClick } from '../../utils/analytics';

/**
 * Шапка приложения - содержит логотип, навигацию и кнопки действий.
 * Навигация вынесена в отдельный компонент Navigation.
 */
const AppHeader: React.FC = () => {
  const { isPro, isAuthenticated, email, logout } = useAuth();
  const navigate = useNavigate();
  const headerRef = useRef<HTMLElement>(null);
  const navRef = useRef<NavigationRef>(null);

  const handleLogout = async () => {
    navRef.current?.closeMenu();
    await logout();
    navigate('/');
  };

  const closeMobileMenu = useCallback(() => {
    navRef.current?.closeMenu();
  }, []);
  
  return (
    <>
      <header ref={headerRef} className="header">
        <div className="container">
          <div className="header-content">
            <Link to="/" className="logo header-logo-link" onClick={closeMobileMenu}>
              <img src={logo} alt="SentiHype" className="logo-img" />
              <AppName className="logo-text" />
            </Link>
            
            {/* Navigation - независимый компонент */}
            <Navigation 
              ref={navRef}
              headerRef={headerRef}
              mobileActions={
                isAuthenticated ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={closeMobileMenu}
                      className="user-info-mobile user-info-card-mobile block"
                    >
                      <div className="user-info-row">
                        <span className="user-email-text">{email}</span>
                        {isPro ? (
                          <span className="plan-badge plan-badge--premium">Premium</span>
                        ) : (
                          <span className="plan-badge plan-badge--free">FREE</span>
                        )}
                      </div>
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="btn btn-outline header-btn-full"
                      type="button"
                    >
                      <LogOut className="logout-icon" />
                      Log Out
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="btn btn-outline header-btn-full header-btn-mobile-gap" onClick={closeMobileMenu}>Log in / Join</Link>
                    <Link
                      to={'/login?redirect=' + encodeURIComponent('/billing?checkout=1')}
                      className="btn btn-upgrade header-btn-full"
                      onClick={() => {
                        trackUpgradeClick('header_mobile_upgrade');
                        closeMobileMenu();
                      }}
                    >
                      Upgrade
                    </Link>
                  </>
                )
              }
            />
            
            {/* Desktop Actions */}
            <div className="header-actions header-actions-desktop">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="user-info-desktop user-info-card-desktop flex items-center space-x-2 px-3 py-1 cursor-pointer hover:opacity-90 transition-opacity"
                  >
                    <span className="user-email-text">{email}</span>
                    {isPro ? (
                      <span className="plan-badge plan-badge--premium">Premium</span>
                    ) : (
                      <span className="plan-badge plan-badge--free">FREE</span>
                    )}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="btn btn-outline"
                    title="Logout"
                  >
                    <LogOut className="logout-icon" />
                    <span className="btn-text-desktop">Log Out</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="btn btn-outline" onClick={closeMobileMenu}>Log in / Join</Link>
                  <Link
                    to={'/login?redirect=' + encodeURIComponent('/billing?checkout=1')}
                    className="btn btn-upgrade btn-upgrade-desktop"
                    onClick={() => {
                      trackUpgradeClick('header_desktop_upgrade');
                      closeMobileMenu();
                    }}
                  >
                    Upgrade
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default AppHeader;
